// Copyright 2026 Mo-Sys Engineering Ltd. All Rights Reserved.
import { openDB } from "idb"; // IndexedDB helper from the `idb` library
import axiosInstance from "../../services/axiosInstance";

// IndexedDB configuration
const DB_NAME = "ImageCacheDB"; // Database name
const STORE_NAME = "thumbnails"; // Object store for cached images
const TTL = 1000 * 60 * 60 * 24; // Time-to-live: 24 hours (ms)

// In-memory cache using a Map
const imageMemoryCache = new Map();

/**
 * Generates a unique cache key for an image.
 * Includes endpoint, image type, id, and optional lastUpdated timestamp.
 */
export const getCacheKey = (id, endpointPrefix, imgType, lastUpdated) =>
  `${endpointPrefix}:${imgType}:${id}:${lastUpdated}`;

/**
 * Opens (or creates) the IndexedDB database and object store.
 */
const getDB = async () => {
  return await openDB(DB_NAME, 1, {
    upgrade(db) {
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "id" });
        store.createIndex("cachedAt", "cachedAt");
      }
    },
  });
};

/**
 * Retrieves a cached image from IndexedDB.
 * Applies TTL expiration only if `lastUpdated` is NOT part of the cache key.
 */
export const getCachedImage = async (cacheKey) => {
  const db = await getDB();
  const entry = await db.get(STORE_NAME, cacheKey);
  // No cache image entry found
  if (!entry) {
    return null;
  }
  // If cacheKey includes lastUpdated, it is assumed to be versioned
  const hasLastUpdated = cacheKey.split(":").length === 4;

  // Apply TTL-based expiration only for non-versioned keys
  if (!hasLastUpdated) {
    const isExpired = Date.now() - entry.cachedAt > TTL;
    if (isExpired) {
      await db.delete(STORE_NAME, cacheKey);
      return null;
    }
  }

  return entry.base64;
};

/**
 * Saves an image to IndexedDB with a timestamp.
 */
export const cacheImage = async (cacheKey, base64) => {
  const db = await getDB();
  await db.put(STORE_NAME, {
    id: cacheKey,
    base64,
    cachedAt: Date.now(),
  });
};

/**
 * Removes a cached image from IndexedDB.
 */
export const clearCachedImage = async (cacheKey) => {
  const db = await getDB();
  await db.delete(STORE_NAME, cacheKey);
};

/**
 * Clears all cached images for an entity (any lastUpdated)
 */
export const clearCachedImagesById = async (id) => {
  const db = await getDB();
  const tx = db.transaction(STORE_NAME, "readwrite");
  const store = tx.objectStore(STORE_NAME);

  const allKeys = await store.getAllKeys();

  for (const key of allKeys) {
    if (typeof key === "string" && key.includes(id)) {
      await store.delete(key);
      imageMemoryCache.delete(key);
    }
  }

  await tx.done;
};

/**
 * Stores a base64 image in the in-memory cache.
 */
export const setInMemoryCache = (cacheKey, base64) => {
  imageMemoryCache.set(cacheKey, base64);
};

/**
 * Marks an image as "not found" in memory to avoid repeat fetches.
 */
export const setImageNotFound = (cacheKey) => {
  imageMemoryCache.set(cacheKey, null);
};

/**
 * Reads the in-memory cache for an image.
 */
export const readMemoryCache = (cacheKey) => {
  if (!imageMemoryCache.has(cacheKey)) {
    return { hit: false };
  }

  const value = imageMemoryCache.get(cacheKey);

  if (value === null) {
    return { hit: true, notFound: true };
  }

  return { hit: true, base64: value };
};

/**
 * Retrieves a cached image and converts it into a File object.
 * Use for re-uploading previously fetched images.
 */
export const getFileFromCachedImage = async (cacheKey) => {
  const base64 = await getCachedImage(cacheKey);
  if (!base64) {
    return null;
  }
  // Convert base64 data URL to File object
  return dataURLtoFile(base64, "cached-image.png");
};

/**
 * Uploads an image file to the backend.
 */
export const uploadImageHelper = async ({ id, endpointPrefix, file }) => {
  if (!file) {
    return null;
  }

  const url = `${endpointPrefix}/image/update/${id}/`;
  const formData = new FormData();
  formData.append("image", file);

  const response = await axiosInstance.post(url, formData, {
    headers: { "Content-Type": "multipart/form-data" },
  });

  return response.data;
};

/**
 * Fetches an image
 * First get the image from IndexedDB cache
 * then fetches from backend if cache not found.
 * Caches the image in both IndexedDB and in-memory cache.
 */
export const fetchImageHelper = async ({
  id,
  endpointPrefix,
  imgType = "Thumbnail",
  lastUpdated = "",
  signal,
}) => {
  // Generate a unique cache key for this image
  const cacheKey = getCacheKey(id, endpointPrefix, imgType, lastUpdated);

  // Check if image is already in-memory cache or marked as not found
  const inMemory = readMemoryCache(cacheKey);
  // skip fetching if found in memory cache
  if (inMemory.hit) {
    return { id, base64: inMemory.notFound ? null : inMemory.base64 };
  }

  // Check persistent IndexedDB cache
  const cached = await getCachedImage(cacheKey);

  if (cached) {
    // Save to memory cache for faster future access
    setInMemoryCache(cacheKey, cached);
    return { id, base64: cached };
  }
  // else fetch image from backend
  try {
    const response = await axiosInstance.get(
      `${endpointPrefix}/image/${id}/${imgType}/`,
      { responseType: "blob", signal },
    );

    const base64 = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(response?.data);
    });
    // Ensure base64 conversion was successful
    if (!base64) {
      throw new Error("Failed to convert image blob to base64");
    }
    // Cache the fetched image
    await cacheImage(cacheKey, base64);
    setInMemoryCache(cacheKey, base64);

    return { id, base64 };
  } catch (error) {
    // Request was aborted skip caching
    if (signal?.aborted) {
      return { id, base64: null };
    }
    // 404 error : Mark image as not found to prevent repeated failed requests
    if (error.response?.status === 404) {
      setImageNotFound(cacheKey);
      return { id, base64: null };
    }
    return { id, base64: null };
  }
};

/**
 * Uploads an image and refreshes related caches.
 * Falls back to cached or remote source images when needed.
 */
export const uploadImageAndRefresh = async ({
  id, // uuid of the type to upload : blockId or showId
  sourceId = null, // source of the image : for blockId > sceneId for showId > blockId
  imgType = "Large",
  endpointPrefix = "", // endpoint prefix for the image upload : show_block or show
  sourceEndpointPrefix = "", // source endpoint prefix to get the source : scene or show_block
  lastUpdated = "",
  defaultImage = null, // Optional default image (base64) e.g. moStudio or laptop png
}) => {
  let file;
  // If a default image is provided, use it to create the image ( ex. moStudio.png or laptop.png)
  if (defaultImage) {
    file = dataURLtoFile(defaultImage, "default-image.png");
  } else {
    // Otherwise, try to get the source image from cache or backend
    const cacheKey = getCacheKey(
      sourceId,
      sourceEndpointPrefix,
      imgType,
      lastUpdated,
    );
    file = await getFileFromCachedImage(cacheKey);

    if (!file) {
      // Fallback: fetch source image directly from backend
      const response = await axiosInstance.get(
        `${sourceEndpointPrefix}/image/${sourceId}/${imgType}/`,
        { responseType: "blob" },
      );
      file = new File([response.data], "source.png", {
        type: response.data?.type || "image/png",
      });
    }
  }
  if (!file) {
    throw new Error("No image file available for upload");
  }

  // upload the image
  const result = await uploadImageHelper({
    id,
    endpointPrefix,
    file,
  });

  // Clear old cached images for the target entity
  await clearCachedImagesById(id);

  return result;
};

/**
 * Converts a base64 data URL into a File object.
 */
export const dataURLtoFile = (dataUrl, filename = "file") => {
  if (typeof dataUrl !== "string" || !dataUrl.includes(",")) {
    throw new Error("Invalid data URL format");
  }

  const [header, base64Data] = dataUrl.split(",");
  const mimeMatch = header.match(/:(.*?);/);
  if (!mimeMatch || !mimeMatch.length) {
    throw new Error("MIME type could not be extracted from data URL");
  }

  const mimeType = mimeMatch[1];
  const binaryString = atob(base64Data);
  const length = binaryString.length;
  const byteArray = new Uint8Array(length);

  for (let i = 0; i < length; i++) {
    byteArray[i] = binaryString.charCodeAt(i);
  }

  return new File([byteArray], filename, { type: mimeType });
};

/**
 * Preloads up to 10 scene thumbnail images.
 * Uses concurrency limits and skips slow network connections.
 */
export const preloadSceneImages = async (sceneList, abortSignal = null) => {
  // Skip preloading images on slow network connections
  const connection = navigator.connection?.effectiveType;
  if (connection && ["2g", "slow-2g"].includes(connection)) {
    return;
  }

  const preloadCount = 10; // Max number of images to preload
  let preloadIndex = 0;
  let fetchedCount = 0; // Track how many images have been fetched
  const concurrencyLimit = 5; // Limit of concurrent fetch operations

  // Uses concurrency limiting to avoid overloading the network.
  const runWithConcurrencyLimit = async (tasks, limit = concurrencyLimit) => {
    const results = [];
    const executing = [];

    for (const task of tasks) {
      const p = task().then((res) => results.push(res));
      executing.push(p);

      // If we've hit the concurrency limit, wait for the fastest one to finish
      if (executing.length >= limit) {
        await Promise.race(executing);
        // Remove completed task from executing list
        executing.splice(
          executing.findIndex((e) => e === p),
          1,
        );
      }
    }
    // Wait for all remaining tasks
    await Promise.all(executing);
    return results;
  };

  // Continue preloading until we hit limit or run out of scenes list
  while (fetchedCount < preloadCount && preloadIndex < sceneList.length) {
    const batch = []; // Batch of fetch tasks to run in parallel

    // Fill a batch with up to `concurrencyLimit` fetch tasks
    while (batch.length < concurrencyLimit && preloadIndex < sceneList.length) {
      const scene = sceneList[preloadIndex++];
      const { scene_uuid: sceneUuid, last_updated: lastUpdated } = scene;
      // Generate a cache key for the image to check if image has arleady been fetched and cached
      const cacheKey = getCacheKey(
        sceneUuid,
        "scene",
        "Thumbnail",
        lastUpdated,
      );
      // Check if the image is already cached
      const cached = await getCachedImage(cacheKey);
      if (!cached) {
        // If not cached, add a fetch task to the batch
        batch.push(async () => {
          try {
            await fetchImageHelper({
              id: sceneUuid,
              endpointPrefix: "scene",
              imgType: "Thumbnail",
              lastUpdated,
              signal: abortSignal,
            });
            fetchedCount++; // Count only successfully fetched images
          } catch (error) {
            // Ignore abort errors, rethrow others
            if (error.name !== "AbortError") {
              throw new Error("failed to fetch image");
            }
          }
        });
      } else {
        // already cached, skip fetching but cache to memory for fast access
        // another batch will be added to meet the desired preloadCount target = 10
        setInMemoryCache(cacheKey, cached);
      }
    }
    // Execute the current batch of fetch tasks
    if (batch.length > 0) {
      await runWithConcurrencyLimit(batch);
    }
  }
};
